import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { hasBlockingQueryError } from '@client/trpc/error-classification';
import { AI_MODEL_TIER_METADATA, AI_PROVIDER_COLORS, type AiModelTier } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Spin, Tag } from 'antd';
import { Layers, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { ModelTierEdit } from './ModelTierEdit';
import { formatContextLength, formatCost } from './utils';

export const Route = createFileRoute('/admin-console/ai/model-tiers')({
    staticData: {
        breadcrumb: {
            title: 'Model tiers',
        },
        ui: {
            icon: Layers,
            title: 'AI Model Tiers',
            subtitle: 'Configure AI models for different capability tiers.',
            menuDescription: 'Configure AI model tiers',
        },
    },
    component: ModelTiers,
});

function ModelTiers() {
    const { trpc } = useTrpc();
    const { isReadOnly } = useAdminModulePermissions('ai');
    const [editingTier, setEditingTier] = useState<AiModelTier | null>(null);

    const { data: tiers, isLoading, isFetching, error, refetch } = trpc.admin.aiModelTier.listAiModelTiers.useQuery();

    const textTiers = tiers?.filter((t) => AI_MODEL_TIER_METADATA[t.tier].category === 'text') ?? [];
    const embeddingTiers = tiers?.filter((t) => AI_MODEL_TIER_METADATA[t.tier].category === 'embedding') ?? [];
    const hasBlockingError = hasBlockingQueryError(error, tiers);

    return (
        <PageController route={Route}>
            <ReadOnlyBanner module="ai" />

            {hasBlockingError ? (
                <InlineQueryError
                    error={error}
                    onRetry={() => void refetch()}
                    retrying={isFetching}
                    title="Couldn't load model tiers"
                />
            ) : (
                <>
                    {error && (
                        <InlineQueryError
                            error={error}
                            onRetry={() => void refetch()}
                            retrying={isFetching}
                            title="Couldn't refresh model tiers"
                            compact
                        />
                    )}

                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <Spin size="large" />
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {/* Text Generation Tiers */}
                            <section>
                                <h2 className="mb-4 font-semibold text-gray-900 text-lg">Text Generation Tiers</h2>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                                    {textTiers.map((tierData) => (
                                        <TierCard
                                            key={tierData.tier}
                                            tier={tierData.tier}
                                            provider={tierData.config.provider}
                                            model={tierData.config.model}
                                            description={tierData.config.description}
                                            isCustomised={tierData.isFromDatabase}
                                            isReadOnly={isReadOnly}
                                            onEdit={() => setEditingTier(tierData.tier)}
                                            pricing={tierData.pricing}
                                        />
                                    ))}
                                </div>
                            </section>

                            {/* Embedding Tiers */}
                            <section>
                                <h2 className="mb-4 font-semibold text-gray-900 text-lg">Embedding Tiers</h2>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                    {embeddingTiers.map((tierData) => (
                                        <TierCard
                                            key={tierData.tier}
                                            tier={tierData.tier}
                                            provider={tierData.config.provider}
                                            model={tierData.config.model}
                                            description={tierData.config.description}
                                            isCustomised={tierData.isFromDatabase}
                                            isReadOnly={isReadOnly}
                                            onEdit={() => setEditingTier(tierData.tier)}
                                            pricing={tierData.pricing}
                                            isEmbedding
                                        />
                                    ))}
                                </div>
                            </section>
                        </div>
                    )}
                </>
            )}

            <ModelTierEdit
                tier={editingTier}
                onClose={() => {
                    setEditingTier(null);
                    void refetch();
                }}
                isReadOnly={isReadOnly}
            />
        </PageController>
    );
}

type PricingInfo = {
    inputCostPer1M: string;
    outputCostPer1M: string;
    contextLength: number;
};

type TierCardProps = {
    tier: AiModelTier;
    provider: string;
    model: string;
    description: string;
    isCustomised: boolean;
    isReadOnly: boolean;
    onEdit: () => void;
    pricing?: PricingInfo;
    isEmbedding?: boolean;
};

function TierCard({
    tier,
    provider,
    model,
    description,
    isCustomised,
    isReadOnly,
    onEdit,
    pricing,
    isEmbedding = false,
}: TierCardProps) {
    const metadata = AI_MODEL_TIER_METADATA[tier];

    // For embedding tiers with $0 output cost, show simplified format
    const isEmbeddingPricing = isEmbedding && pricing && parseFloat(pricing.outputCostPer1M) === 0;

    return (
        <Card
            className="transition-shadow hover:shadow-md"
            title={
                <div className="flex items-center justify-between">
                    <span className="font-semibold">{metadata.label}</span>
                    {isCustomised && (
                        <Tag color="orange" className="ml-2">
                            <RotateCcw size={12} className="mr-1 inline" />
                            Custom
                        </Tag>
                    )}
                </div>
            }
            extra={<Button.Edit size="small" tooltip={isReadOnly ? 'View tier' : 'Edit tier'} onClick={onEdit} />}
        >
            <p className="mb-3 text-gray-500 text-sm">{metadata.description}</p>
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-400 text-xs">Provider:</span>
                    <Tag variant="outlined" color={AI_PROVIDER_COLORS[provider] || 'default'}>
                        {provider}
                    </Tag>
                </div>
                <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-400 text-xs">Model:</span>
                    <code className="rounded bg-gray-100 px-2 py-0.5 text-gray-700 text-xs">{model}</code>
                </div>
                {description && <p className="mt-2 text-gray-500 text-xs italic">{description}</p>}
            </div>

            {pricing && (
                <div className="mt-3 border-gray-100 border-t pt-3">
                    {isEmbeddingPricing ? (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-500 text-xs">
                            <span>
                                <span className="font-medium text-gray-600">Cost:</span>{' '}
                                {formatCost(pricing.inputCostPer1M)}/1M tokens
                            </span>
                            <span className="text-gray-300">|</span>
                            <span>
                                <span className="font-medium text-gray-600">Context:</span>{' '}
                                {formatContextLength(pricing.contextLength)}
                            </span>
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-500 text-xs">
                            <span>
                                <span className="font-medium text-gray-600">Input:</span>{' '}
                                {formatCost(pricing.inputCostPer1M)}/1M
                            </span>
                            <span className="text-gray-300">|</span>
                            <span>
                                <span className="font-medium text-gray-600">Output:</span>{' '}
                                {formatCost(pricing.outputCostPer1M)}/1M
                            </span>
                            <span className="text-gray-300">|</span>
                            <span>
                                <span className="font-medium text-gray-600">Context:</span>{' '}
                                {formatContextLength(pricing.contextLength)}
                            </span>
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
}
